import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import '../App.css';
import './ProfilePage.css';

function ProfilePage({ onGameSelect, onNavigateToReview, onStartNewGame }) {
  const { user, userProfile } = useAuth();
  const [friends, setFriends] = useState([]);
  const [loadingFriends, setLoadingFriends] = useState(true);

  useEffect(() => {
    if (user) {
      loadFriends();
    }
  }, [user]);


  const loadFriends = async () => {
    if (!user) return;
    
    try {
      setLoadingFriends(true);
      
      // For hosts: Get players from games they created
      // For players: Get users who participated in the same games
      let gameIds = [];

      if (userProfile?.role === 'host') {
        // Get games created by this user
        const { data: createdGames, error: gamesError } = await supabase
          .from('games')
          .select('id')
          .eq('created_by', user.id);

        if (gamesError) {
          console.error('Error loading created games:', gamesError);
          setFriends([]);
          return;
        }

        if (!createdGames || createdGames.length === 0) {
          setFriends([]);
          return;
        }

        gameIds = createdGames.map(g => g.id);
      } else {
        // For players: Get games they participated in
        const { data: participants, error: participantsError } = await supabase
          .from('game_participants')
          .select('game_id')
          .eq('user_id', user.id);

        if (participantsError) {
          console.error('Error loading game participants:', participantsError);
          setFriends([]);
          return;
        }

        if (!participants || participants.length === 0) {
          setFriends([]);
          return;
        }

        gameIds = participants.map(p => p.game_id);
      }

      // Get all participants in those games (excluding the current user)
      const { data: otherParticipants, error: otherError } = await supabase
        .from('game_participants')
        .select('user_id')
        .in('game_id', gameIds)
        .neq('user_id', user.id);

      if (otherError) {
        console.error('Error loading other participants:', otherError);
        setFriends([]);
        return;
      }

      // Get unique user IDs
      const uniqueUserIds = [...new Set(otherParticipants.map(p => p.user_id))];

      if (uniqueUserIds.length === 0) {
        setFriends([]);
        return;
      }

      // Get user profiles for those users
      const { data: userProfiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('id, full_name, email, role')
        .in('id', uniqueUserIds)
        .limit(20); // Increased limit since hosts might have many players

      if (profilesError) {
        console.error('Error loading user profiles:', profilesError);
        setFriends([]);
      } else {
        setFriends(userProfiles || []);
      }
    } catch (error) {
      console.error('Error loading friends:', error);
      setFriends([]);
    } finally {
      setLoadingFriends(false);
    }
  };


  const getUserInitials = () => {
    if (!user) return '?';
    const email = user.email || '';
    const name = userProfile?.full_name || '';
    
    if (name) {
      const parts = name.trim().split(' ');
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return name[0].toUpperCase();
    }
    
    if (email) {
      return email[0].toUpperCase();
    }
    
    return '?';
  };

  if (!user) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#060010',
        color: '#fff',
        padding: '2rem'
      }}>
        <div>Please sign in to view your profile.</div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: '#060010',
      color: '#fff',
      padding: '2rem',
      paddingTop: 'calc(2rem + 60px + 2rem)'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Top Section: Profile and Friends Side by Side */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '2rem',
          marginBottom: '2rem'
        }}
        className="profile-friends-grid"
        >
          {/* Profile Card - Left Half */}
          <div className="section" style={{ 
            background: '#242323',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.5rem',
            padding: '0'
          }}>
            {/* Avatar Section */}
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Avatar 
                style={{
                  width: '128px',
                  height: '128px',
                  border: '4px solid white',
                  borderRadius: '50%',
                  overflow: 'hidden'
                }}
              >
                {userProfile?.avatar_url && (
                  <AvatarImage 
                    src={userProfile.avatar_url}
                    alt={userProfile?.full_name || 'Profile'}
                  />
                )}
                <AvatarFallback 
                  style={{
                    backgroundColor: '#D702F6',
                    color: 'white',
                    fontSize: '3rem',
                    fontWeight: 600,
                    borderRadius: '50%',
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    lineHeight: '1'
                  }}
                >
                  {getUserInitials()}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* User Info */}
            <div style={{ textAlign: 'center' }}>
              <h1 style={{
                fontSize: '2rem',
                fontWeight: 700,
                margin: '0 0 0.5rem 0',
                color: '#fff'
              }}>
                {userProfile?.full_name || 'User'}
              </h1>
              <p style={{
                fontSize: '1rem',
                color: 'rgba(255, 255, 255, 0.7)',
                margin: '0 0 0.5rem 0'
              }}>
                {user.email}
              </p>
              {userProfile?.role && (
                <div style={{
                  display: 'inline-block',
                  padding: '0.25rem 0.75rem',
                  borderRadius: '12px',
                  background: userProfile.role === 'host' ? 'rgba(215, 2, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                  color: userProfile.role === 'host' ? '#D702F6' : 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.875rem',
                  fontWeight: 600
                }}>
                  {userProfile.role === 'host' ? 'Host' : 'Player'}
                </div>
              )}
            </div>

          </div>
          </div>

          {/* Friends Section - Right Half */}
          <div className="section" style={{ 
            background: '#242323',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
          }}>
            <div className="section-header" style={{
              borderBottomColor: 'rgba(255, 255, 255, 0.1)',
              marginBottom: '1.5rem',
              paddingBottom: '1rem'
            }}>
              <h2 style={{ color: '#fff', margin: 0, fontSize: '1.8rem' }}>Friends ({friends.length})</h2>
            </div>
            
            {loadingFriends ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Loading friends...
              </div>
            ) : friends.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                <p>No friends yet.</p>
                <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
                  Friends will appear here when you play games together!
                </p>
              </div>
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '1rem'
              }}>
                {friends.map((friend) => {
                  const getFriendInitials = () => {
                    const name = friend.full_name || '';
                    const email = friend.email || '';
                    
                    if (name) {
                      const parts = name.trim().split(' ');
                      if (parts.length >= 2) {
                        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
                      }
                      return name[0].toUpperCase();
                    }
                    
                    if (email) {
                      return email[0].toUpperCase();
                    }
                    
                    return '?';
                  };

                  return (
                    <div
                      key={friend.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        padding: '1rem',
                        background: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '8px',
                        transition: 'transform 0.2s ease, border-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'translateX(4px)';
                        e.currentTarget.style.borderColor = 'rgba(215, 2, 246, 0.5)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'translateX(0)';
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      }}
                    >
                      <Avatar 
                        style={{
                          width: '48px',
                          height: '48px',
                          border: '2px solid white',
                          borderRadius: '50%',
                          overflow: 'hidden',
                          flexShrink: 0
                        }}
                      >
                        <AvatarFallback 
                          style={{
                            backgroundColor: '#D702F6',
                            color: 'white',
                            fontSize: '1rem',
                            fontWeight: 600,
                            borderRadius: '50%',
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: '1'
                          }}
                        >
                          {getFriendInitials()}
                        </AvatarFallback>
                      </Avatar>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          color: '#fff',
                          fontSize: '1rem',
                          fontWeight: 600,
                          marginBottom: '0.25rem'
                        }}>
                          {friend.full_name || friend.email || 'Unknown User'}
                        </div>
                        {friend.full_name && (
                          <div style={{
                            color: 'rgba(255, 255, 255, 0.6)',
                            fontSize: '0.875rem',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}>
                            {friend.email}
                          </div>
                        )}
                        {friend.role && (
                          <div style={{
                            display: 'inline-block',
                            marginTop: '0.25rem',
                            padding: '0.125rem 0.5rem',
                            borderRadius: '8px',
                            background: friend.role === 'host' ? 'rgba(215, 2, 246, 0.2)' : 'rgba(255, 255, 255, 0.1)',
                            color: friend.role === 'host' ? '#D702F6' : 'rgba(255, 255, 255, 0.8)',
                            fontSize: '0.75rem',
                            fontWeight: 500
                          }}>
                            {friend.role === 'host' ? 'Host' : 'Player'}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;

